import { memo, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import hljs from 'highlight.js';
import ReactJsonView from '@microlink/react-json-view';

import * as roles from './roles';
import { createPortal } from 'react-dom';

(window as any).hljs = hljs;

const url = new URL(window.location.href);
const STORAGE_ROLE_PREFIX = 'kevin:custom-role:';

interface CustomRole {
    key: string;
    name: string;
    prefill: string;
    createdAt: number;
}

const customRoles: CustomRole[] = [];
loadCustomRoles();

function loadCustomRoles() {
    const count = localStorage.length;

    for (let i = 0; i < count; i++) {
        const key = localStorage.key(i);

        try {
            if (key && key.startsWith(STORAGE_ROLE_PREFIX)) {
                const value = JSON.parse(localStorage.getItem(key)!);

                if (value) {
                    const { name, prefill, createdAt } = value;
                    customRoles.push({ key, name, prefill, createdAt });
                }
            }
        } catch (e) {
            console.error(e);
        }
    }
}

function nextAvailableKeyFor(name: string) {
    const slug = name.toLowerCase().replaceAll(/[^a-zA-Z0-9-_]*/g, '-').trim();
    let attempt = slug;
    let num = 1;

    while (true) {
        const exists = !!localStorage.getItem(STORAGE_ROLE_PREFIX + attempt);
        if (!exists) {
            return STORAGE_ROLE_PREFIX + attempt;
        }

        attempt = slug + "-" + (num++);
    }
}

function createCustomRole(name: string, prefill: string) {
    const key = nextAvailableKeyFor(name);
    const record = { key, name, prefill, createdAt: Date.now() };

    customRoles.push(record);
    saveRole(record);
}

function saveRole(data: CustomRole) {
    const idx = customRoles.findIndex(role => role.key === data.key);
    if (idx >= 0) {
        customRoles[idx] = { ...customRoles[idx], ...data };
        localStorage.setItem(data.key, JSON.stringify(data));
    } else {
        window.alert('Saving failed; role was not found.');
    }
}

let token: string | null = localStorage.getItem('token');
if (url.searchParams && url.searchParams.has('token')) {
    token = url.searchParams.get('token');
}

// If we have a token, store it in local storage
if (token) {
    localStorage.setItem('token', token);
} else {
    // If we still don't have a token, alert the user
    window.alert('Wrong link used!');
}

window.onbeforeunload = () => {
    return 'Are you sure you want to leave?';
};



function stringToBlob(text, contentType = "application/octet-stream") {
    return new Blob([text], { type: contentType });
}

function downloadBlob(blob, fname, newTab = true) {
    const link = document.createElement('a');

    link.href = URL.createObjectURL(blob);

    if (newTab) {
        link.target = "_blank";
    }

    // The 'download' attribute is crucial. It tells the browser to download
    // the file instead of navigating to it. You can suggest a filename.
    link.download = fname || "download";

    // Append to body to ensure it can be clicked, then click, then remove
    // document.body.appendChild(link);
    link.click();
};


export default function App() {
    const [messages, setMessages] = useState<MessageData[]>([]);

    const [question, _setQuestion] = useState('');
    const [currentModel, _setCurrentModel] = useState<string>(localStorage.getItem("kevin:last_model") || "google/gemini-2.5-pro");
    const [currentRole, setCurrentRole] = useState(roles.defaultRole);
    const [asking, setAsking] = useState(false);
    const [currentSignal, setCurrentSignal] = useState<AbortController | null>(null);
    const textArea = useRef<HTMLTextAreaElement>(null);
    const [fontSize, setFontSize] = useState('md');
    const [temperature, setTemperature] = useState(0.7);
    const [editingKevin, setEditingKevin] = useState<string | null>(null); // Only set if we edit an existing Kevin.
    const [creatingKevin, setCreatingKevin] = useState(false); // This one's for non-existing creation. New Kevin.
    const [compress, setCompress] = useState(false);
    const [canSearch, setCanSearch] = useState(false);

    const isCustomRole = currentRole.startsWith(STORAGE_ROLE_PREFIX);
    const role = isCustomRole ? customRoles.find(r => r.key === currentRole) : roles.roles[currentRole];

    const setCurrentModel = (m: string) => {
        _setCurrentModel(m);
        localStorage.setItem('kevin:last_model', m);
    };

    const setQuestion = (q: string) => {
        _setQuestion(q);

        // Resize content
        if (textArea.current) {
            textArea.current.style.height = '100%';
        }
    };

    useEffect(() => {
        if (textArea.current) {
            textArea.current.focus();
        }
    }, [textArea]);

    const askKevin = async (role: any, text: string, temperature: number, regeneratingUUID?: string) => {
        text = text.trim();

        if (text.toLowerCase().startsWith('/undo')) {
            setMessages(all => all.slice(0, -1));
            return;
        }

        if (text.toLowerCase().startsWith('/retry')) {
            const lastUserMsg = messages.findLastIndex(m => m.role === 'user');
            if (lastUserMsg >= 0) {
                const msgData = messages[lastUserMsg];
                setMessages(all => all.slice(0, lastUserMsg));
                text = msgData.content;
            } else {
                window.alert("That didn't work.");
                return;
            }

            // Continue as normal with new msg
        }

        const el = document.getElementsByClassName('scroll-wrap')[0];
        el.scrollIntoView({ behavior: "smooth", block: "end", inline: "end" });

        const controller = new AbortController();
        const signal = controller.signal;
        setCurrentSignal(controller);

        setQuestion('');
        setAsking(true);

        let requestHistory: any[] = [];
        let respKey: string = crypto.randomUUID();
        let newMessage: MessageData;

        if (regeneratingUUID) {
            respKey = regeneratingUUID;

            const responseIdx = messages.findIndex(msg => msg.uuid === respKey);
            requestHistory = messages.slice(0, responseIdx);

            newMessage = { ...messages[responseIdx], loading: true, content: '', aborted: false, model: currentModel };

            // We have to update the target message; surgically. Must replace the couple fields in there.
            setMessages(all => all.map(msg => msg.uuid !== respKey ? msg :
                ({ ...newMessage })));
        } else {
            const q: MessageData = { uuid: crypto.randomUUID(), role: 'user', content: text, aborted: false, model: '', loading: false };
            newMessage = { uuid: respKey, role: 'assistant', content: '', aborted: false, model: currentModel, loading: true };

            const newMessages: MessageData[] = [...messages, q, newMessage];
            setMessages(newMessages);
            requestHistory = newMessages.slice(0, -1);
        }

        try {
            let id = undefined; // OpenRouter generation ID

            await davinci(requestHistory, data => {
                const delta = typeof data === 'string' ? data : data?.choices?.[0]?.delta;

                if (data && typeof data === 'object') {
                    if (data.usage) {
                        newMessage.usage = data.usage;
                    }

                    if (data.id && !id) {
                        id = data.id;
                    }
                }

                if (delta && typeof delta === 'object') {
                    if (delta.annotations?.length && !newMessage.annotations) {
                        newMessage.annotations = delta?.annotations;
                    }

                    if (delta.tool_calls?.length && !newMessage.tools) {
                        newMessage.tools = delta?.tool_calls;
                    }

                    if (delta.content) {
                        newMessage.content += delta.content;
                    }

                    if (delta.reasoning) {
                        newMessage.reasoning += delta.reasoning;
                    }
                }

                setMessages(all => [...all.map(m => m.uuid !== respKey ? m : ({ ...newMessage }))]);
            }, signal, controller, currentModel, role, temperature, compress, canSearch);

            // Only if we have a reason given, otherwise it's a user cancellation
            if (signal.aborted && signal.reason) {
                signal.throwIfAborted();
            }
        } catch (e) {
            console.error(e);

            setMessages(list => [...list.map(item => item.uuid !== respKey ? item :
                { ...item, content: item.content + "\n\n" + e, error: e })]);
        }

        setAsking(false);
        setCurrentSignal(null);

        setMessages(list => [...list.map(item => item.uuid !== respKey ? item :
            { ...item, loading: false })]);
    };

    const abortCurrentRequest = () => {
        if (currentSignal) {
            try {
                currentSignal.abort();
            } catch (e) {
                console.error(e);
            }

            setCurrentSignal(null);

            // Mark the last message as aborted
            const lastMessage = messages[messages.length - 1];
            if (lastMessage.role === 'assistant') {
                setMessages(list => [...list.slice(0, -1), { ...lastMessage, aborted: true, loading: false }]);
            }
        }
    };

    let fontScale = 1;
    if (fontSize === 'lg') {
        fontScale = 1;
    } else if (fontSize === 'md') {
        fontScale = 0.9;
    } else if (fontSize === 'sm') {
        fontScale = 0.8;
    }

    let temps: number[] = [];
    for (let i = 0.0; i <= 1.01; i += 0.05) {
        temps.push(i);
    }

    const closeKevinModal = () => {
        setEditingKevin(null);
        setCreatingKevin(false);
    };

    const openCreateModal = () => {
        setEditingKevin(null);
        setCreatingKevin(true);
    };

    const openEditModal = () => {
        setEditingKevin(currentRole);
        setCreatingKevin(false);
    };

    const createKevin = (name: string, prefill: string) => {
        if (editingKevin) {
            saveRole({ ...role, name, prefill } as any);
            closeKevinModal();
        } else {
            createCustomRole(name, prefill);
            closeKevinModal();
        }
    };

    const importConversation = async (ev?: React.ChangeEvent<HTMLInputElement>) => {
        const options = {
            types: [
                {
                    description: "Conversation JSON data",
                    accept: {
                        "application/json": [".json"],
                    },
                },
            ],
            excludeAcceptAllOption: true,
            multiple: false,
        };

        // const [chosen] = await (window as any).showOpenFilePicker(options);
        const chosen = ev?.currentTarget?.files?.item(0);
        if (chosen) {
            // const fileData = await chosen.getFile();
            const poep = await chosen.text();
            const msgdata = JSON.parse(poep);

            if (!Array.isArray(msgdata)) {
                alert('Invalid queue data.');
                return;
            }


            setMessages(msgdata);
        } else if (true||window.confirm("Want to import from URL instead?")) {
            const url = window.prompt("Enter JSON raw URL");
            if (url) {
                try {
                    const r = await fetch(url);
                    const txt = await r.text();
                    const msgdata = JSON.parse(txt);

                    if (!Array.isArray(msgdata)) {
                        alert('Invalid queue data.');
                        return;
                    }

                    setMessages(msgdata);
                } catch (e) {
                    window.alert(`Import failed: ${e}`);
                }
            }
        }
    };

    const regenerateMessage = (uuid: string) => {
        askKevin(role, '', temperature, uuid);
    };

    return (
        <>
            <header className="header">
                <h1 className='logo'><span className='logo-icon'>K</span>evin</h1>


                <div style={{ display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'center', cursor: 'pointer', alignSelf: 'stretch' }}>
                    <IconZip width="20" height="20" fill={compress ? "white" : "gray"} onClick={() => setCompress(v => !v)} />
                    <IconGlobe width="20" height="20" fill={canSearch ? "white" : "#80808060"} onClick={() => setCanSearch(v => !v)} />
                    
                        <IconFolderOpen width="20" height="20" fill={"white"} onClick={()=>importConversation()} />
                        {/*<input type="filee" onChange={ev => importConversation(ev).catch(e=> window.alert(e))} style={{ display: 'none' }} />*/}
                    
                    <IconExport width="20" height="20" fill={"white"} onClick={() => downloadBlob(stringToBlob(JSON.stringify(messages, undefined, 4)), `kevin-conversation-${Date.now()}.json`, true)} />

                    <select value={`${temperature.toFixed(2)}`} onChange={ev => setTemperature(Number(ev.currentTarget.value))}>
                        {temps.map(t => <option key={t.toFixed(2)} value={t.toFixed(2)}>{t.toFixed(2)}</option>)}
                    </select>

                    <IconH width="14" height="14" fill={fontSize === 'sm' ? "white" : "gray"} onClick={() => setFontSize('sm')} />
                    <IconH width="18" height="18" fill={fontSize === 'md' ? "white" : "gray"} onClick={() => setFontSize('md')} />
                    <IconH width="22" height="22" fill={fontSize === 'lg' ? "white" : "gray"} onClick={() => setFontSize('lg')} />
                </div>
            </header>

            <div id="app" style={{ "--font-scale": fontScale } as any}>
                <div className='home'>
                    <div className="kevincontainer">
                        <MessageList messages={messages} regenerateMessage={regenerateMessage} />

                        <div className="command">
                            <form onSubmitCapture={e => { askKevin(role, question, temperature); e.preventDefault(); return false; }}>
                                <textarea
                                    style={{ height: '100%' }}
                                    value={question}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            askKevin(role, question, temperature);
                                            e.preventDefault();
                                            return false;
                                        }
                                    }}
                                    onInput={e => setQuestion(e.currentTarget.value)}
                                    placeholder={asking ? "Kevin is thinking really hard..." : "Enter your Kevinput here. Be accurate and concise: Kevin has a learning disability."}
                                    className="kevinput"
                                    ref={textArea} />
                            </form>

                            {asking && <button onClick={abortCurrentRequest} className="abort">Abort</button>}


                            <div className='sponsor'>
                                <div className='roleplay'>
                                    <select className='role' value={currentRole} onChange={e => e.target.value === '__createKevin' ? openCreateModal() : setCurrentRole(e.target.value)}>
                                        <option disabled>---- OG Kevins ----</option>
                                        {Object.entries(roles.roles).map(([key, role]) => (
                                            <option key={key} value={key}>{role.name}</option>
                                        ))}

                                        <option disabled>---- My Kevins ----</option>
                                        {customRoles.map(role => (
                                            <option key={role.key} value={role.key}>{role.name}</option>
                                        ))}

                                        <option value="__createKevin">[+] Create new Kevin</option>
                                    </select>

                                    {isCustomRole && <IconPenEdit width="18" height="18" fill="#d6d6d6" onClick={() => openEditModal()} />}
                                </div>

                                <div className='models'>
                                    <ModelPicker currentModel={currentModel} setCurrentModel={setCurrentModel} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {(editingKevin || creatingKevin) && <CreateKevinModal roleKey={editingKevin || null} editing={editingKevin ? role : null} open close={() => closeKevinModal()} save={createKevin} />}
        </>
    );
}

const CreateKevinModal = (props: any) => {
    const { roleKey, editing, open, close, save } = props;

    const [name, setName] = useState(editing ? editing.name : '');
    const [prompt, setPrompt] = useState(editing ? editing.prefill : '');

    const submit = () => {
        if (name && prompt) {
            save(name, prompt);
        }

        return true;
    };

    return open && (<div id="create-kevin-modal" className="modal-overlay visible">
        <div className="modal-content">
            <div className="modal-header">
                <h2>{roleKey ? 'Configure' : 'Create'} My Kevin</h2>

                <a className="modal-close" aria-label="Close modal" onClick={close}>
                    &times;
                </a>
            </div>

            <form id="create-kevin-form" onSubmit={submit}>
                <div className="modal-body">
                    <div className="form-group">
                        <label htmlFor="kevin-name">Name</label>
                        <input
                            value={name}
                            onChange={ev => setName(ev.currentTarget.value)}
                            type="text"
                            id="kevin-name"
                            name="name"
                            placeholder="e.g., 'BFF Kevin'"
                            required
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="kevin-prompt">Prompt</label>
                        <textarea
                            value={prompt}
                            onChange={ev => setPrompt(ev.currentTarget.value)}
                            id="kevin-prompt"
                            name="prompt"
                            rows={8}
                            placeholder="Tell Kevin how to act. Do you want more Kevin, less Kevin, two Kevin..?"
                            required
                        />
                    </div>
                </div>

                <div className="modal-footer">
                    <button type="button" className="btn btn-secondary modal-cancel" onClick={close}>
                        Cancel
                    </button>

                    <button type="submit" className="btn btn-primary">
                        {roleKey ? 'Save' : 'Create'}
                    </button>
                </div>
            </form>
        </div>
    </div>
    );
};


const ActionSelectModal = (props: any) => {
    const { open = true, close, options = [], title } = props;

    return open && (<div id="create-kevin-modal" className="modal-overlay visible">
        <div className="modal-content" style={{ maxWidth: 420 }}>
            <div className="modal-header">
                <h3>{title}</h3>

                <a className="modal-close" aria-label="Close modal" onClick={close}>
                    &times;
                </a>
            </div>

            <div className="modal-body modal-action-list">
                {options.map(opt => (
                    <button className="modal-action-button" onClick={opt.onClick || undefined}>
                        {opt.label}
                    </button>
                ))}
            </div>
        </div>
    </div>
    );
};

async function copyNodeTextToClipboard(selectorOrElement, trigger?: HTMLElement) {
    const node = typeof (selectorOrElement) === 'string' ?
        document.getElementById(selectorOrElement)
        : selectorOrElement;

    if (!node) {
        console.error(`Element with selector "${selectorOrElement}" not found.`);
        return;
    }

    try {
        await navigator.clipboard.writeText(node.innerText);

        if (trigger) {
            const old = trigger.innerText;
            trigger.innerText = "copied!";
            trigger.classList.add('copied');

            window.setTimeout(() => {
                trigger.innerText = old;
                trigger.classList.remove('copied');
            }, 2000);
        }
    } catch (err) {
        console.error('Failed to copy: ', err);
    }
}

function thisfn(u, a, eid) {
    copyNodeTextToClipboard(eid, u);
}
(window as any).codeCopyHandler = thisfn;

function postProcess(msg: MessageData): MessageData {
    const md = marked.parse(msg.content ?? msg.reasoning ?? '', { gfm: true }) as string;
    let markdownHtml = DOMPurify.sanitize(md);

    // Find hex color codes
    const hexRegex = /#[0-9A-Fa-f]{6,8}/g;
    markdownHtml = markdownHtml.replace(hexRegex, (sub, args) => {
        return `<label id="col${sub.substring(1)}"><input type="color" value="${sub}" readonly />${sub}</label> <span class="copy" onclick="javascript:codeCopyHandler(this, event, 'col${sub.substring(1)}')">copy</span>`;
    });

    const p = new DOMParser();
    const doc = p.parseFromString(markdownHtml, "text/html");

    const dataViewers: DataViewerConfig[] = [];

    let idx = 1;

    for (const codeEl of Array.from(doc.querySelectorAll("pre > code").values())) {
        const rawCode = (codeEl as HTMLElement).innerText.trim();
        const lang = codeEl.className.includes("language-") ? [codeEl.className.replace("language-", "")] : undefined;

        const codeId = msg.uuid.replaceAll('-', '') + "X" + idx++;
        codeEl.id = codeId;

        // Custom JSON handling, we inject this via portals.
        if (lang?.at(0) === 'json') {
            try {
                dataViewers.push({
                    elemId: codeId,
                    json: JSON.parse(rawCode),
                });

                codeEl.innerHTML = "";

                // Swap out the <pre> for a regular <div>
                const wrapper = document.createElement('div');
                wrapper.id = codeId;
                wrapper.className = "json-viewer";

                const pre = codeEl.parentElement!;

                pre.parentNode!.replaceChild(wrapper, pre);

                continue;
            } catch (e) {
            }
        }

        const { value, language } = hljs.highlightAuto(rawCode, lang);
        codeEl.setAttribute("data-inferred", language ?? "");

        if (rawCode.includes("\n")) { // Multi-line code block; we embed the copy button
            codeEl.innerHTML = value;
            codeEl.parentElement!.innerHTML += `\n<span class="copy" onclick="javascript:codeCopyHandler(this, event, '${codeId}')">copy</span>\n`;
        } else { // Single-line code block, so we append the button
            codeEl.innerHTML = value;

            const copyNode = doc.createElement('span');
            codeEl.after(copyNode);

            copyNode.outerHTML = `<span class="copy" onclick="javascript:codeCopyHandler(this, event, '${codeId}')">copy</span>`;
        }
    }

    markdownHtml = (doc.body as HTMLElement).innerHTML;

    return { ...msg, html: markdownHtml, dataViewers };
}

interface MessageData {
    uuid: string;
    role: 'assistant' | 'user' | 'system' | 'developer';
    content: string;
    reasoning?: string;
    aborted: boolean;
    model: string;
    loading: boolean;
    html?: string;
    error?: any;
    dataViewers?: DataViewerConfig[];
    usage?: any;
    annotations?: any[];
    tools?: any[];
}

interface DataViewerConfig {
    elemId: string; // For portal
    json: any;
}

interface MessageListProps {
    messages: MessageData[];
    regenerateMessage: any;
}

const MessageList = (props: MessageListProps) => {
    const { messages, regenerateMessage } = props;

    const [showOptionsFor, setShowOptionsFor] = useState<any | null>(null);
    const convo = messages.filter(m => m.role !== 'system');

    const messageOptions = [
        {
            label: "Regenerate",
            onClick() {
                regenerateMessage(showOptionsFor);
            }
        },
        {
            label: "Copy contents",
            onClick() {
                navigator.clipboard.writeText(convo.find(msg => msg.uuid === showOptionsFor)!.content);
                setShowOptionsFor(null);
            }
        },
        {
            label: "Copy raw contents",
            onClick() {
                navigator.clipboard.writeText(convo.find(msg => msg.uuid === showOptionsFor)!.content);
                setShowOptionsFor(null);
            }
        }
    ];

    return (<>
        <div className="kevinbox">
            <div className="scroll-wrap">
                {convo.map((msg, key) => (
                    <ChatMessage key={msg.uuid} message={msg} onOptions={() => setShowOptionsFor(msg.uuid)} selected={showOptionsFor === msg.uuid} />
                ))}
            </div>
        </div>

        <ActionSelectModal
            title="Message Actions"
            open={showOptionsFor !== null}
            close={() => setShowOptionsFor(null)}
            options={showOptionsFor !== null ? messageOptions : null}
        />
    </>);
};

const theme = {
    // --- Monochromatic Palette ---
    // Default background
    base00: "rgba(40, 44, 52, 1)",
    // Lighter background
    base01: "rgba(50, 56, 66, 1)",
    // Selection background
    base02: "rgba(63, 68, 79, 1)",
    // Comments (.hljs-comment)
    base03: "rgba(113, 124, 141, 1)",
    // Dark foreground
    base04: "rgba(138, 147, 165, 1)",
    // Default foreground (braces, commas)
    base05: "rgba(171, 178, 191, 1)",
    // Light foreground
    base06: "rgba(205, 210, 218, 1)",
    // Lightest foreground
    base07: "rgba(255, 255, 255, 1)",

    // --- Syntax Palette ---
    // Not used by react-json-viewer for typical JSON
    base08: "rgba(123, 204, 227, 1)",
    // Integers, Booleans, null (.hljs-literal, .hljs-number)
    base09: "rgba(221, 254, 144, 1)",
    // Not used
    base0A: "rgba(248, 211, 129, 1)",
    // Strings (.hljs-string)
    base0B: "rgba(221, 254, 144, 1)",
    // Not used
    base0C: "rgba(123, 204, 227, 1)",
    // Keys (.hljs-property)
    base0D: "rgba(230, 162, 123, 1)",
    // Not used
    base0E: "rgba(243, 177, 114, 1)",
    // Not used
    base0F: "rgba(218, 192, 251, 1)",
};

const ChatMessage = (props: { message: MessageData; onOptions: any; selected: boolean; }) => {
    const { message: msgRaw, onOptions, selected } = props;
    const msg = useMemo(() => postProcess(msgRaw), [msgRaw.content, msgRaw.reasoning]);

    const [showRaw, setShowRaw] = useState(false);

    let innerHtml = msg.html! || "";
    let contentClasses = "chatcontent ";

    if (selected) {
        contentClasses += "selected ";
    }

    if (msgRaw.loading && !msg.content) {
        innerHtml = "<i>Think, think, think...</i>";
    }

    if (msgRaw.error) {
        contentClasses += "has-error ";
        innerHtml += "That didn't work :-(<br/><br/><pre>";

        const e = msgRaw.error!;
        if (typeof (e) === 'string') {
            innerHtml += e;
        } else if ('toString' in e) {
            innerHtml += e.toString();
        } else {
            innerHtml += `${e}`;
        }

        innerHtml += "</pre>";
    }

    let modelName = "";
    let modelData: any = null;

    if (msgRaw.model) {
        modelName = msgRaw.model;

        if (ALL_MODELS.length) {
            modelData = ALL_MODELS.find(m => m.id === msgRaw.model);
            modelName = modelData?.name;
        }
    }

    const annotations = msgRaw?.annotations || [];
    const tools = msgRaw?.tools || [];
    const costEquiv = (modelData && msgRaw?.usage?.prompt_tokens) ? (`$${(msgRaw.usage.prompt_tokens * parseFloat(modelData.pricing.prompt)).toFixed(6)} / $${(msgRaw.usage.completion_tokens * parseFloat(modelData.pricing.completion)).toFixed(6)}`) : "idk";

    //{"prompt_tokens":921,"completion_tokens":39,"total_tokens":960}
    return (
        <div className={`chat chat-${msg.role} ` + (selected ? 'selected' : '')}>
            {/* {msg.role === 'assistant' && <span className='model' style={{ float: 'right' }}>Model: {msg.model}</span>} */}

            <div className='chatname'>
                {msgRaw.role === 'assistant' ? modelName : "You"}
            </div>

            {msgRaw.usage && <div className='chat-attachment-top' onClick={() => alert(costEquiv)}>
                {msgRaw.usage.total_tokens} t ({msgRaw.usage.prompt_tokens} / {msgRaw.usage.completion_tokens})
            </div>}

            <div className={contentClasses}>
                {showRaw ?
                    <pre style={{ whiteSpace: 'pre' }}>{msg.content}</pre>
                    :
                    <div className='inner' dangerouslySetInnerHTML={{ __html: innerHtml }} />}

                <div className="tts-button" onClick={onOptions}>
                    <MenuIcon width={16} height={16} />
                </div>

                {
                    // msg.role === 'assistant' && !msg.loading && !msg.error ? <TextToSpeechButton text={msg.content} /> : null
                }
            </div>

            {annotations?.length ? <div className="annotations">
                <IconGlobe width="16" height="16" fill="#5fbdf8AA" />
                {annotations.map(ann => (
                    <a className="annotation" target="_blank" href={ann?.url_citation?.url}>{ann?.url_citation?.title}</a>
                ))}
            </div> : null}

            {tools?.length ? <div className="annotations">
                {tools.map(tool => (
                    <span>{tool?.function?.name} {tool?.function?.arguments}</span>
                ))}
            </div> : null}

            {msgRaw.aborted && <div className='aborted'>Response interrupted.</div>}

            {msg.dataViewers?.filter(e => document.getElementById(e.elemId))?.map(viewer => createPortal(<ReactJsonView key={viewer.elemId} name={null} displayDataTypes={false} theme={theme} src={viewer.json} />, document.getElementById(viewer.elemId)!, viewer.elemId))}
        </div>
    );
};

const IconH = (props: any) => <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M0 64C0 46.3 14.3 32 32 32l48 0 48 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-16 0 0 112 224 0 0-112-16 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l48 0 48 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-16 0 0 144 0 176 16 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-48 0-48 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l16 0 0-144-224 0 0 144 16 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-48 0-48 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l16 0 0-176L48 96 32 96C14.3 96 0 81.7 0 64z" /></svg>;
const IconZip = (props: any) => (<svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-288-128 0c-17.7 0-32-14.3-32-32L224 0 64 0zM256 0l0 128 128 0L256 0zM96 48c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16l32 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-32 0c-8.8 0-16-7.2-16-16zm-6.3 71.8c3.7-14 16.4-23.8 30.9-23.8l14.8 0c14.5 0 27.2 9.7 30.9 23.8l23.5 88.2c1.4 5.4 2.1 10.9 2.1 16.4c0 35.2-28.8 63.7-64 63.7s-64-28.5-64-63.7c0-5.5 .7-11.1 2.1-16.4l23.5-88.2zM112 336c-8.8 0-16 7.2-16 16s7.2 16 16 16l32 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-32 0z" /></svg>);
const IconFolderOpen = (props: any) => (<svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path d="M88.7 223.8L0 375.8 0 96C0 60.7 28.7 32 64 32l117.5 0c17 0 33.3 6.7 45.3 18.7l26.5 26.5c12 12 28.3 18.7 45.3 18.7L416 96c35.3 0 64 28.7 64 64l0 32-336 0c-22.8 0-43.8 12.1-55.3 31.8zm27.6 16.1C122.1 230 132.6 224 144 224l400 0c11.5 0 22 6.1 27.7 16.1s5.7 22.2-.1 32.1l-112 192C453.9 474 443.4 480 432 480L32 480c-11.5 0-22-6.1-27.7-16.1s-5.7-22.2 .1-32.1l112-192z" /></svg>);
const MenuIcon = (props: any) => (<svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M320 208C289.1 208 264 182.9 264 152C264 121.1 289.1 96 320 96C350.9 96 376 121.1 376 152C376 182.9 350.9 208 320 208zM320 432C350.9 432 376 457.1 376 488C376 518.9 350.9 544 320 544C289.1 544 264 518.9 264 488C264 457.1 289.1 432 320 432zM376 320C376 350.9 350.9 376 320 376C289.1 376 264 350.9 264 320C264 289.1 289.1 264 320 264C350.9 264 376 289.1 376 320z" /></svg>);


export const IconPlayCircle = (props: any) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}><path d="M0 256a256 256 0 1 1 512 0A256 256 0 1 1 0 256zM188.3 147.1c-7.6 4.2-12.3 12.3-12.3 20.9l0 176c0 8.7 4.7 16.7 12.3 20.9s16.8 4.1 24.3-.5l144-88c7.1-4.4 11.5-12.1 11.5-20.5s-4.4-16.1-11.5-20.5l-144-88c-7.4-4.5-16.7-4.7-24.3-.5z" /></svg>);
export const IconPauseCircle = (props: any) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" {...props}><path d="M48 64C21.5 64 0 85.5 0 112L0 400c0 26.5 21.5 48 48 48l32 0c26.5 0 48-21.5 48-48l0-288c0-26.5-21.5-48-48-48L48 64zm192 0c-26.5 0-48 21.5-48 48l0 288c0 26.5 21.5 48 48 48l32 0c26.5 0 48-21.5 48-48l0-288c0-26.5-21.5-48-48-48l-32 0z" /></svg>);
const IconPenEdit = (props: any) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}><path d="M471.6 21.7c-21.9-21.9-57.3-21.9-79.2 0L362.3 51.7l97.9 97.9 30.1-30.1c21.9-21.9 21.9-57.3 0-79.2L471.6 21.7zm-299.2 220c-6.1 6.1-10.8 13.6-13.5 21.9l-29.6 88.8c-2.9 8.6-.6 18.1 5.8 24.6s15.9 8.7 24.6 5.8l88.8-29.6c8.2-2.7 15.7-7.4 21.9-13.5L437.7 172.3 339.7 74.3 172.4 241.7zM96 64C43 64 0 107 0 160L0 416c0 53 43 96 96 96l256 0c53 0 96-43 96-96l0-96c0-17.7-14.3-32-32-32s-32 14.3-32 32l0 96c0 17.7-14.3 32-32 32L96 448c-17.7 0-32-14.3-32-32l0-256c0-17.7 14.3-32 32-32l96 0c17.7 0 32-14.3 32-32s-14.3-32-32-32L96 64z" /></svg>);
const IconGlobe = (props: any) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" {...props}><path d="M352 256c0 22.2-1.2 43.6-3.3 64l-185.3 0c-2.2-20.4-3.3-41.8-3.3-64s1.2-43.6 3.3-64l185.3 0c2.2 20.4 3.3 41.8 3.3 64zm28.8-64l123.1 0c5.3 20.5 8.1 41.9 8.1 64s-2.8 43.5-8.1 64l-123.1 0c2.1-20.6 3.2-42 3.2-64s-1.1-43.4-3.2-64zm112.6-32l-116.7 0c-10-63.9-29.8-117.4-55.3-151.6c78.3 20.7 142 77.5 171.9 151.6zm-149.1 0l-176.6 0c6.1-36.4 15.5-68.6 27-94.7c10.5-23.6 22.2-40.7 33.5-51.5C239.4 3.2 248.7 0 256 0s16.6 3.2 27.8 13.8c11.3 10.8 23 27.9 33.5 51.5c11.6 26 20.9 58.2 27 94.7zm-209 0L18.6 160C48.6 85.9 112.2 29.1 190.6 8.4C165.1 42.6 145.3 96.1 135.3 160zM8.1 192l123.1 0c-2.1 20.6-3.2 42-3.2 64s1.1 43.4 3.2 64L8.1 320C2.8 299.5 0 278.1 0 256s2.8-43.5 8.1-64zM194.7 446.6c-11.6-26-20.9-58.2-27-94.6l176.6 0c-6.1 36.4-15.5 68.6-27 94.6c-10.5 23.6-22.2 40.7-33.5 51.5C272.6 508.8 263.3 512 256 512s-16.6-3.2-27.8-13.8c-11.3-10.8-23-27.9-33.5-51.5zM135.3 352c10 63.9 29.8 117.4 55.3 151.6C112.2 482.9 48.6 426.1 18.6 352l116.7 0zm358.1 0c-30 74.1-93.6 130.9-171.9 151.6c25.5-34.2 45.2-87.7 55.3-151.6l116.7 0z" /></svg>);
const IconExport = (props: any) => (<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" {...props}><path d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 128-168 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l168 0 0 112c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zM384 336l0-48 110.1 0-39-39c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l80 80c9.4 9.4 9.4 24.6 0 33.9l-80 80c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l39-39L384 336zm0-208l-128 0L256 0 384 128z" /></svg>);

const formatModel = (model: any) => {
    const inputCost = (Number(model.pricing.prompt) * 1000000).toFixed(2);
    const outputCost = (Number(model.pricing.completion) * 1000000).toFixed(2);

    // Model.providers is an array of objects, each with `p50_throughput` as field.
    if (model.providers) {
        const sortedProviders = [...model.providers].sort((a, b) => (b.p50_throughput || 0) - (a.p50_throughput || 0));
        const bestProvider = sortedProviders[0];

        if (bestProvider?.p50_throughput) {
            const throughput = bestProvider.p50_throughput.toFixed(0);
            return `${model.name} (${throughput} t/s) (\$${inputCost} / \$${outputCost})`;
        }
    }

    return `${model.name} (\$${inputCost} / \$${outputCost})`;
};

const ALL_MODELS: any[] = [];

const ModelPicker = (props: any) => {
    const { currentModel, setCurrentModel } = props;

    const [models, setModels] = useState<any[]>([]);

    useEffect(() => {
        (async () => {
            const queued = fetch("https://webby.sh/openrouter").then(r => r.json());

            const data = await fetch("https://openrouter.ai/api/v1/models").then(r => r.json());
            let models: any[] = data.data;

            const providers: any[] = await queued;

            for (const m of models) {
                const providerData = providers.filter(mod => mod.model?.permaslug === m.canonical_slug);

                if (providerData?.length) {
                    m.providers = providerData.filter(e => e.stats).map(p => ({ ...p.stats, quantization: p.quantization }));
                    m.name = providerData.find(p => p?.model?.short_name)?.model?.short_name || m.name;
                }
            }

            models = models.filter(m => m.architecture.input_modalities.includes("text")).sort((a, b) => b.created - a.created);
            setModels(models);

            if (!ALL_MODELS.length) {
                ALL_MODELS.push(...models);
                console.log(ALL_MODELS.find(m => m.id === currentModel));
            }
        })();
    }, []);

    const currentModelData = models.find(e => e.id === currentModel);

    return (
        <select value={currentModel} onChange={ev => setCurrentModel(ev.target.value)} title={currentModelData?.id || null}>
            {models.map(m => <option value={m.id} key={m.id}>{formatModel(m)}</option>)}
        </select>
    );
};


async function davinci(convo: MessageData[], onFragment, signal: AbortSignal, controller: AbortController, model, roleData, temperature = 0.7, compress = false, canSearch = false) {
    // Prepare messages for OpenAI
    const messages = convo.filter(m => !m.error).map(m => ({
        role: m.role,
        content: m.content,// + (m.aborted ? "\n\n- (Gebruiker heeft het antwoord onderbroken, mogelijk wegens je onjuiste interpretatie van zijn of haar vraag) -" : ""),
    }));

    // First user message is sometimes given
    if (roleData.prefillUser) {
        messages.unshift({
            role: 'user',
            content: roleData.prefillUser,
        });
    }

    // Insert system message at the front
    messages.unshift({
        role: 'system',
        content: roleData.prefill + "\n\nIMPORTANT: only make use of tools with good reason or when you're getting things done, never by default or at random. If uncertain, always ask first.",
    });

    const stream = true;//!UNSTREAMABLE.includes(model);

    const tools = [
        {
            "type": "function",
            "function": {
                "name": "github_code_search",
                "description": "Searches for code on GitHub using a single query string. GitHub has its own specific format for queries. For more information on GitHub code search syntax, refer to the [GitHub Docs](https://docs.github.com/en/search-github/github-code-search/understanding-github-code-search-syntax).",
                "parameters": {
                    "type": "object",
                    "properties": {
                        "query": {
                            "type": "string",
                            "description": "The search string to look for, following GitHub's code search syntax. For example, 'sparse index' would find documents containing both terms. [github.com](https://docs.github.com/en/search-github/github-code-search/understanding-github-code-search-syntax)"
                        }
                    },
                    "required": [
                        "query"
                    ]
                }
            }
        }
    ];

    const response = await fetch("https://openrouter.ai/api/v1/chat/completions", {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-Title': 'Kevin',
        },
        body: JSON.stringify({
            messages,
            model,
            // max_completion_tokens: 4092,
            reasoning: { enabled: true, effort: 'high' },
            temperature,
            // logit_bias: { 4269: -100 },
            // logprobs: true,
            // top_logprobs: 1,
            stream,
            // provider: {
            //     sort: 'throughput'
            // },
            transforms: compress ? ["middle-out"] : undefined,
            plugins: canSearch ? [{ id: "web" }] : undefined,
            // tools,
        }),
        signal
    });

    if (!stream) {
        const json = await response.json();

        if (json.error) {
            throw new Error(json.error.message);
        }

        onFragment(json.choices[0].message.content);
        return;
    }

    const reader = response.body!.getReader();
    let buffer = new Uint8Array();

    while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (!value) {
            reader.cancel();
            break;
        }

        // Append value to buffer (in case decoder needs to split a multi-byte character)
        const tmp = new Uint8Array(buffer.length + value.length);
        tmp.set(buffer);
        tmp.set(value, buffer.length);
        buffer = tmp;

        try {
            // Decode buffer
            const decoder = new TextDecoder('utf8');
            const txt = decoder.decode(buffer);

            let fragments: string[] = [];

            txt.split('\n').forEach(l => {
                l = l.trim();

                if (l.length === 0 || l.includes('[DONE]')) {
                    return;
                }

                if (l.endsWith("PROCESSING")) {
                    return;
                }

                if (l.startsWith('data: {') && l.endsWith('}')) {
                    console.log(l);
                    fragments.push(JSON.parse(l.substring(6)));
                } else if (l.startsWith('{') && l.endsWith('}')) {
                    console.log(l);
                    const json = JSON.parse(l);

                    if (json?.error) {
                        controller.abort(new Error(JSON.stringify(json.error)));
                    }
                } else {
                    throw new Error(`Invalid JSON found: ${l}`);
                }
            });

            fragments.forEach(onFragment);

            // Clear buffer
            buffer = new Uint8Array();
        } catch (e) {
            console.error(e);
            // Ignore, we'll try again next packet
        }

        if (done || signal.aborted) {
            reader.cancel();
            break;
        }
    }
}
